import { Avatar } from '@roger/ui-components';
import type { AttachmentUrl } from '../../hooks/chat/useAttachmentUrls';
import type { AttachmentOut, MessageOut } from '../../services/api';
import { PREVIEW_MAX_BYTES } from '../../utils/attachments';
import { AttachmentChip } from './AttachmentChip';
import { AttachmentTile } from './AttachmentTile';
import { initialsOf } from './initials';

const isPreviewKind = (a: AttachmentOut) =>
  a.content_type.startsWith('image/') || a.content_type === 'application/pdf';

/** The spec's render table, in its order. */
// eslint-disable-next-line react-refresh/only-export-components -- the render table sits beside the one component that uses it
export function attachmentRender(
  a: AttachmentOut
): 'tile' | 'tile-pending' | 'chip' | 'chip-no-preview' {
  if (!isPreviewKind(a)) return 'chip';
  if (a.status === 'ready') return 'tile';
  if (a.status === 'failed' || a.size_bytes > PREVIEW_MAX_BYTES)
    return 'chip-no-preview';
  return 'tile-pending';
}

export function MessageItem({
  message,
  isOwn,
  urls = {},
  onOpenAttachment,
  onDownloadAttachment = () => {},
}: {
  message: MessageOut;
  isOwn: boolean;
  urls?: Record<string, AttachmentUrl | null | undefined>;
  onOpenAttachment?: (tiles: AttachmentOut[], index: number) => void;
  onDownloadAttachment?: (id: string) => void;
}) {
  const attachments = message.attachments ?? [];
  // The lightbox moves across this message's ready tiles only.
  const tiles = attachments.filter(a => attachmentRender(a) === 'tile');

  return (
    <div
      data-seq={message.seq}
      className={`flex gap-2 px-3 py-1.5 ${isOwn ? 'bg-primary/[0.06]' : ''}`}
    >
      <Avatar
        size='sm'
        initials={initialsOf(message.sender_name)}
        className='mt-0.5 shrink-0'
      />
      <div className='min-w-0 flex-1'>
        <div className='flex items-baseline gap-2'>
          <span className='text-sm font-semibold'>{message.sender_name}</span>
          <span className='text-[11px] font-light text-muted-foreground'>
            {new Date(message.created_at).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
        </div>
        {message.body && (
          <p
            data-testid='message-body'
            className='whitespace-pre-wrap break-words text-sm'
          >
            {message.body}
          </p>
        )}
        {attachments.length > 0 && (
          <div className='mt-1 flex flex-wrap gap-2'>
            {attachments.map(a => {
              const kind = attachmentRender(a);
              if (kind === 'chip' || kind === 'chip-no-preview') {
                return (
                  <AttachmentChip
                    key={a.id}
                    attachment={a}
                    noPreview={kind === 'chip-no-preview'}
                    onDownload={onDownloadAttachment}
                  />
                );
              }
              return (
                <AttachmentTile
                  key={a.id}
                  attachment={a}
                  url={urls[a.id]}
                  alone={attachments.length === 1}
                  onOpen={
                    kind === 'tile' && onOpenAttachment
                      ? () => onOpenAttachment(tiles, tiles.indexOf(a))
                      : undefined
                  }
                  onDownload={onDownloadAttachment}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
